/**
 * #56 PR 2c (DEC-363) — Operations → Incidents, Rules and Recipients, and the incident page
 * the alert email links to (/ops/incidents/:id).
 *
 *   Incidents   live by default; acknowledge (stops reminders) or resolve, with a note
 *   Rules       the alert switch, each rule's thresholds, cooldowns and snooze, and
 *               "Send test alert" (to you only)
 *   Recipients  pick a Paelode platform admin (never a typed address), their minimum
 *               severity and quiet hours. Nobody is emailed until one is added.
 *
 * Changing anything needs MANAGE_PLATFORM_ALERTS, which only PLATFORM_ADMIN holds; the
 * buttons are hidden otherwise and the API refuses regardless.
 */
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from 'src/components/ui/table';
import { Button } from 'src/components/ui/button';
import { Pill, fieldStyle, labelStyle, subText } from '../../ui/bits';
import { useAdminProfile } from '../../lib/use-profile';
import {
  useAlertIncident,
  useAlertIncidents,
  useAlertRecipients,
  useAlertRules,
  useAlertSettings,
  useAlertWrite,
  useRecipientCandidates,
  type AlertIncident,
  type AlertRecipient,
  type AlertRule,
  type AlertSeverity,
  type IncidentFilter,
  type QuietHours,
  type RuleParams,
} from './alerts-hooks';
import {
  INCIDENT_STATUS_LABEL,
  NOTIFICATION_KIND_LABEL,
  NOTIFICATION_STATUS_LABEL,
  QUIET_ZONES,
  RESOLVE_REASON_LABEL,
  incidentTone,
  isFixtureAccount,
  paramLabel,
  partLabel,
  quietLabel,
  severityPhrase,
} from './alerts-labels';
import { timeAgo } from './labels';
import { apiErrorText } from '@lodex-pro/web-shared';

/** MANAGE_PLATFORM_ALERTS is PLATFORM_ADMIN only (D-11); the API is the real gate. */
export function useCanManageAlerts(): boolean {
  const profile = useAdminProfile(true);
  return profile.data?.profile.role === 'PLATFORM_ADMIN';
}

function errText(err: unknown): string {
  return apiErrorText(err, 'That did not work.');
}

const linkButton: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: 'var(--color-link)',
  cursor: 'pointer',
  fontSize: '13px',
};

// ── Incidents ───────────────────────────────────────────────────────────────────────

function IncidentActions({ i }: { i: AlertIncident }): React.JSX.Element | null {
  const canManage = useCanManageAlerts();
  const [note, setNote] = React.useState('');
  const ack = useAlertWrite<{ note?: string }>(`/incidents/${i.id}/ack`, 'PATCH');
  const resolve = useAlertWrite<{ note?: string }>(`/incidents/${i.id}/resolve`, 'PATCH');
  if (!canManage || i.status === 'RESOLVED') return null;
  const body = note.trim() === '' ? {} : { note: note.trim() };
  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
      <input
        className="keep-case"
        aria-label={`Note for ${i.ruleName}`}
        placeholder="Note (optional)"
        value={note}
        maxLength={500}
        onChange={(e): void => setNote(e.target.value)}
        style={{ ...fieldStyle, width: '180px', minHeight: '32px' }}
      />
      {i.status === 'OPEN' && (
        <Button
          size="sm"
          variant="outline"
          disabled={ack.isPending}
          onClick={(): void =>
            ack.run(
              body,
              () => toast.success('Acknowledged — no more reminders'),
              (e) => toast.error(errText(e)),
            )
          }
        >
          Acknowledge
        </Button>
      )}
      <Button
        size="sm"
        variant="outline"
        disabled={resolve.isPending}
        onClick={(): void =>
          resolve.run(
            body,
            () => toast.success('Resolved'),
            (e) => toast.error(errText(e)),
          )
        }
      >
        Resolve
      </Button>
    </div>
  );
}

export function IncidentsTab(): React.JSX.Element {
  const [filter, setFilter] = React.useState<IncidentFilter>('LIVE');
  const { data, isLoading, isError } = useAlertIncidents(filter);
  const navigate = useNavigate();
  return (
    <div>
      <label style={{ display: 'inline-block', minWidth: '200px', marginBottom: '12px' }}>
        <span style={labelStyle}>Show</span>
        <select
          aria-label="Incidents to show"
          value={filter}
          onChange={(e): void => setFilter(e.target.value as IncidentFilter)}
          style={fieldStyle}
        >
          <option value="LIVE">Open and acknowledged</option>
          <option value="RESOLVED">Resolved</option>
          <option value="ALL">All</option>
        </select>
      </label>
      {isLoading ? (
        <p style={subText}>Loading…</p>
      ) : isError || data === undefined ? (
        <p role="alert" style={subText}>
          Could not load incidents.
        </p>
      ) : data.incidents.length === 0 ? (
        <p style={{ fontSize: '14px' }}>
          {filter === 'LIVE' ? 'No open incidents.' : 'No incidents.'}
        </p>
      ) : (
        <Table aria-label="Incidents">
          <TableHeader>
            <TableRow>
              <TableHead>Problem</TableHead>
              <TableHead>Tenant</TableHead>
              <TableHead>Since</TableHead>
              <TableHead>State</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.incidents.map((i) => (
              <TableRow key={i.id}>
                <TableCell>
                  <Pill
                    label={i.severity}
                    tone={i.severity === 'CRITICAL' ? 'danger' : 'warning'}
                  />{' '}
                  <button
                    type="button"
                    style={linkButton}
                    onClick={(): void => void navigate(`/ops/incidents/${i.id}`)}
                  >
                    {i.ruleName}
                  </button>
                  <div style={subText}>{i.summary}</div>
                </TableCell>
                <TableCell>{i.tenantName ?? '—'}</TableCell>
                <TableCell>
                  {timeAgo(i.firstFiredAt)}
                  <div style={subText}>last seen {timeAgo(i.lastFiredAt)}</div>
                </TableCell>
                <TableCell>
                  <Pill label={INCIDENT_STATUS_LABEL[i.status]} tone={incidentTone(i.status)} />
                  {i.resolveReason !== null && (
                    <div style={subText}>
                      {RESOLVE_REASON_LABEL[i.resolveReason] ?? i.resolveReason}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <IncidentActions i={i} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

export function IncidentPage(): React.JSX.Element {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useAlertIncident(id);
  return (
    <div style={{ padding: '24px', maxWidth: '900px' }}>
      <button type="button" style={linkButton} onClick={(): void => void navigate('/ops')}>
        ← Operations
      </button>
      {isLoading ? (
        <p style={subText}>Loading…</p>
      ) : isError || data === undefined ? (
        <p role="alert" style={subText}>
          Could not load this incident.
        </p>
      ) : (
        <>
          <h1 style={{ fontSize: '20px', fontWeight: 700, margin: '12px 0 4px' }}>
            {data.incident.ruleName}
          </h1>
          <p style={{ fontSize: '14px', margin: '0 0 8px' }}>{data.incident.summary}</p>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <Pill
              label={data.incident.severity}
              tone={data.incident.severity === 'CRITICAL' ? 'danger' : 'warning'}
            />
            <Pill
              label={INCIDENT_STATUS_LABEL[data.incident.status]}
              tone={incidentTone(data.incident.status)}
            />
          </div>
          <dl
            style={{
              fontSize: '14px',
              display: 'grid',
              gridTemplateColumns: '160px 1fr',
              gap: '4px 12px',
            }}
          >
            <dt style={subText}>Tenant</dt>
            <dd>{data.incident.tenantName ?? '—'}</dd>
            <dt style={subText}>First seen</dt>
            <dd>{new Date(data.incident.firstFiredAt).toLocaleString()}</dd>
            <dt style={subText}>Last seen</dt>
            <dd>{new Date(data.incident.lastFiredAt).toLocaleString()}</dd>
            {data.incident.ackedBy !== null && (
              <>
                <dt style={subText}>Acknowledged by</dt>
                <dd>{data.incident.ackedBy}</dd>
              </>
            )}
            {data.incident.resolvedAt !== null && (
              <>
                <dt style={subText}>Resolved</dt>
                <dd>
                  {new Date(data.incident.resolvedAt).toLocaleString()}
                  {data.incident.resolveReason !== null &&
                    ` — ${RESOLVE_REASON_LABEL[data.incident.resolveReason] ?? ''}`}
                  {data.incident.resolvedBy !== null && ` by ${data.incident.resolvedBy}`}
                </dd>
              </>
            )}
            {data.incident.note !== null && (
              <>
                <dt style={subText}>Note</dt>
                <dd>{data.incident.note}</dd>
              </>
            )}
          </dl>
          <div style={{ margin: '12px 0' }}>
            <IncidentActions i={data.incident} />
          </div>
          <h2 style={{ fontSize: '15px', fontWeight: 600, margin: '20px 0 8px' }}>Emails</h2>
          {data.notifications.length === 0 ? (
            <p style={subText}>No emails for this incident.</p>
          ) : (
            <Table aria-label="Emails">
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Why</TableHead>
                  <TableHead>To</TableHead>
                  <TableHead>Result</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.notifications.map((n) => (
                  <TableRow key={n.id}>
                    <TableCell>{timeAgo(n.at)}</TableCell>
                    <TableCell>{NOTIFICATION_KIND_LABEL[n.kind]}</TableCell>
                    <TableCell>{n.recipientName ?? '—'}</TableCell>
                    <TableCell>
                      {NOTIFICATION_STATUS_LABEL[n.status]}
                      {n.error !== null && <div style={subText}>{n.error}</div>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      )}
    </div>
  );
}

// ── Rules ───────────────────────────────────────────────────────────────────────────

function AlertSwitch(): React.JSX.Element | null {
  const canManage = useCanManageAlerts();
  const { data } = useAlertSettings();
  const write = useAlertWrite<{ alertsEnabled: boolean }>('/settings', 'PATCH');
  if (data === undefined) return null;
  return (
    <div
      style={{
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        padding: '12px',
        borderRadius: 'var(--radius-card)',
        backgroundColor: 'var(--color-bg-secondary)',
        marginBottom: '16px',
      }}
    >
      <Pill
        label={data.alertsEnabled ? 'Alerts on' : 'Alerts off'}
        tone={data.alertsEnabled ? 'positive' : 'danger'}
      />
      <span style={{ fontSize: '14px' }}>
        {data.alertsEnabled
          ? 'Problems open incidents and email the recipients.'
          : 'Nothing is evaluated or emailed. For emergencies only.'}
      </span>
      {canManage && (
        <Button
          size="sm"
          variant="outline"
          onClick={(): void => {
            if (data.alertsEnabled && !window.confirm('Turn ops alerts off for this environment?'))
              return;
            write.run({ alertsEnabled: !data.alertsEnabled }, () =>
              toast.success(data.alertsEnabled ? 'Alerts turned off' : 'Alerts turned on'),
            );
          }}
        >
          {data.alertsEnabled ? 'Turn alerts off' : 'Turn alerts on'}
        </Button>
      )}
    </div>
  );
}

function ParamsEditor({
  params,
  onChange,
  ruleName,
}: {
  params: RuleParams;
  onChange: (p: RuleParams) => void;
  ruleName: string;
}): React.JSX.Element {
  const num = (label: string, value: number, set: (n: number) => void): React.JSX.Element => (
    <label key={label} style={{ display: 'block', minWidth: '180px' }}>
      <span style={labelStyle}>{label}</span>
      <input
        type="number"
        aria-label={`${ruleName}: ${label}`}
        value={Number.isFinite(value) ? value : ''}
        onChange={(e): void => set(Number(e.target.value))}
        style={{ ...fieldStyle, minHeight: '32px' }}
      />
    </label>
  );
  return (
    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
      {Object.entries(params).map(([key, value]) =>
        typeof value === 'number' ? (
          num(paramLabel(key), value, (n) => onChange({ ...params, [key]: n }))
        ) : (
          <fieldset key={key} style={{ border: 'none', padding: 0, margin: 0, width: '100%' }}>
            <legend style={labelStyle}>{paramLabel(key)}</legend>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {Object.entries(value).map(([part, n]) =>
                num(partLabel(part), n, (v) =>
                  onChange({ ...params, [key]: { ...value, [part]: v } }),
                ),
              )}
            </div>
          </fieldset>
        ),
      )}
    </div>
  );
}

function RuleRow({ rule }: { rule: AlertRule }): React.JSX.Element {
  const canManage = useCanManageAlerts();
  const [editing, setEditing] = React.useState(false);
  const [params, setParams] = React.useState<RuleParams>(rule.params);
  const [cw, setCw] = React.useState(rule.cooldownWarningMinutes);
  const [cc, setCc] = React.useState(rule.cooldownCriticalMinutes);
  const save = useAlertWrite<Record<string, unknown>>(`/rules/${rule.id}`, 'PATCH');
  const test = useAlertWrite<Record<string, never>, { status: string }>(
    `/rules/${rule.id}/test`,
    'POST',
  );
  const snoozed = rule.snoozeUntil !== null && new Date(rule.snoozeUntil).getTime() > Date.now();
  const snooze = (hours: number | null): void =>
    save.run(
      {
        snoozeUntil: hours === null ? null : new Date(Date.now() + hours * 3_600_000).toISOString(),
      },
      () => toast.success(hours === null ? 'Rule woken' : `Snoozed for ${String(hours)} h`),
      (e) => toast.error(errText(e)),
    );
  return (
    <TableRow>
      <TableCell>
        <div style={{ fontWeight: 600 }}>{rule.name}</div>
        <div style={subText}>
          Reminder every {rule.cooldownWarningMinutes} min (warning) /{' '}
          {rule.cooldownCriticalMinutes} min (critical)
          {rule.liveIncidents > 0 && ` · ${String(rule.liveIncidents)} open`}
        </div>
        {editing && (
          <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <ParamsEditor params={params} onChange={setParams} ruleName={rule.name} />
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <label>
                <span style={labelStyle}>Remind every (warning, min)</span>
                <input
                  type="number"
                  aria-label={`${rule.name}: warning reminder minutes`}
                  value={cw}
                  min={15}
                  onChange={(e): void => setCw(Number(e.target.value))}
                  style={{ ...fieldStyle, minHeight: '32px' }}
                />
              </label>
              <label>
                <span style={labelStyle}>Remind every (critical, min)</span>
                <input
                  type="number"
                  aria-label={`${rule.name}: critical reminder minutes`}
                  value={cc}
                  min={15}
                  onChange={(e): void => setCc(Number(e.target.value))}
                  style={{ ...fieldStyle, minHeight: '32px' }}
                />
              </label>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                size="sm"
                disabled={save.isPending}
                onClick={(): void =>
                  save.run(
                    { params, cooldownWarningMinutes: cw, cooldownCriticalMinutes: cc },
                    () => {
                      toast.success('Rule saved');
                      setEditing(false);
                    },
                    (e) => toast.error(errText(e)),
                  )
                }
              >
                Save
              </Button>
              <Button size="sm" variant="outline" onClick={(): void => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </TableCell>
      <TableCell>
        {!rule.enabled ? (
          <Pill label="Off" tone="muted" />
        ) : snoozed ? (
          <Pill
            label={`Snoozed until ${new Date(rule.snoozeUntil ?? '').toLocaleString()}`}
            tone="warning"
          />
        ) : (
          <Pill label="On" tone="positive" />
        )}
      </TableCell>
      <TableCell>
        {canManage && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Button
              size="sm"
              variant="outline"
              onClick={(): void =>
                save.run({ enabled: !rule.enabled }, () =>
                  toast.success(rule.enabled ? 'Rule off' : 'Rule on'),
                )
              }
            >
              {rule.enabled ? 'Turn off' : 'Turn on'}
            </Button>
            {snoozed ? (
              <Button size="sm" variant="outline" onClick={(): void => snooze(null)}>
                Wake
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={(): void => snooze(24)}>
                Snooze 24 h
              </Button>
            )}
            {!editing && (
              <Button
                size="sm"
                variant="outline"
                onClick={(): void => {
                  setParams(rule.params);
                  setCw(rule.cooldownWarningMinutes);
                  setCc(rule.cooldownCriticalMinutes);
                  setEditing(true);
                }}
              >
                Edit
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              disabled={test.isPending}
              onClick={(): void =>
                test.run(
                  {},
                  (res) =>
                    res.status === 'SENT'
                      ? toast.success('Test alert sent to you')
                      : toast.error('The test alert could not be sent'),
                  (e) => toast.error(errText(e)),
                )
              }
            >
              Send test alert
            </Button>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}

export function RulesTab(): React.JSX.Element {
  const { data, isLoading, isError } = useAlertRules();
  return (
    <div>
      <AlertSwitch />
      {isLoading ? (
        <p style={subText}>Loading…</p>
      ) : isError || data === undefined ? (
        <p role="alert" style={subText}>
          Could not load rules.
        </p>
      ) : (
        <Table aria-label="Rules">
          <TableHeader>
            <TableRow>
              <TableHead>Rule</TableHead>
              <TableHead>State</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.rules.map((r) => (
              <RuleRow key={r.id} rule={r} />
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// ── Recipients ──────────────────────────────────────────────────────────────────────

function QuietHoursFields({
  value,
  onChange,
  who,
}: {
  value: QuietHours | null;
  onChange: (q: QuietHours | null) => void;
  who: string;
}): React.JSX.Element {
  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <label>
        <input
          type="checkbox"
          aria-label={`${who}: quiet hours`}
          checked={value !== null}
          onChange={(e): void =>
            onChange(
              e.target.checked
                ? { start: '22:00', end: '06:00', timeZone: 'America/Chicago' }
                : null,
            )
          }
        />{' '}
        Quiet hours
      </label>
      {value !== null && (
        <>
          <input
            type="time"
            aria-label={`${who}: quiet from`}
            value={value.start}
            onChange={(e): void => onChange({ ...value, start: e.target.value })}
            style={{ ...fieldStyle, width: '120px', minHeight: '32px' }}
          />
          <input
            type="time"
            aria-label={`${who}: quiet until`}
            value={value.end}
            onChange={(e): void => onChange({ ...value, end: e.target.value })}
            style={{ ...fieldStyle, width: '120px', minHeight: '32px' }}
          />
          <select
            aria-label={`${who}: quiet hours time zone`}
            value={value.timeZone}
            onChange={(e): void => onChange({ ...value, timeZone: e.target.value })}
            style={{ ...fieldStyle, width: '190px', minHeight: '32px' }}
          >
            {QUIET_ZONES.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </select>
        </>
      )}
    </div>
  );
}

/** The signed-in admin's user id, once the profile has loaded. */
function useMyUserId(): string | undefined {
  return useAdminProfile(true).data?.profile.userId;
}

function RecipientRow({ r, isMe }: { r: AlertRecipient; isMe: boolean }): React.JSX.Element {
  const canManage = useCanManageAlerts();
  const save = useAlertWrite<Record<string, unknown>>(`/recipients/${r.id}`, 'PATCH');
  const remove = useAlertWrite<Record<string, never>>(`/recipients/${r.id}`, 'DELETE');
  return (
    <TableRow>
      <TableCell>
        <div style={{ fontWeight: 600, display: 'flex', gap: '6px', alignItems: 'center' }}>
          {r.name}
          {isMe && <Pill label="You" tone="positive" />}
          {isFixtureAccount(r.email) && <Pill label="Test account" />}
        </div>
        <div style={subText}>{r.email}</div>
      </TableCell>
      <TableCell>
        {canManage ? (
          <select
            aria-label={`${r.name}: alerts to receive`}
            value={r.minSeverityEmail}
            onChange={(e): void =>
              save.run({ minSeverityEmail: e.target.value }, () => toast.success('Saved'))
            }
            style={{ ...fieldStyle, minHeight: '32px' }}
          >
            <option value="WARNING">Warnings and critical</option>
            <option value="CRITICAL">Critical only</option>
          </select>
        ) : r.minSeverityEmail === 'CRITICAL' ? (
          'Critical only'
        ) : (
          'Warnings and critical'
        )}
      </TableCell>
      <TableCell>
        {canManage ? (
          <QuietHoursFields
            value={r.quietHours}
            who={r.name}
            onChange={(q): void =>
              save.run(
                { quietHours: q },
                () => toast.success('Saved'),
                (e) => toast.error(errText(e)),
              )
            }
          />
        ) : (
          quietLabel(r.quietHours)
        )}
      </TableCell>
      <TableCell>
        {canManage && (
          <Button
            size="sm"
            variant="outline"
            onClick={(): void => {
              if (!window.confirm(`Stop sending ops alerts to ${r.name}?`)) return;
              remove.run({}, () => toast.success('Removed'));
            }}
          >
            Remove
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

function AddRecipient({ myUserId }: { myUserId: string | undefined }): React.JSX.Element | null {
  const { data } = useRecipientCandidates();
  const [userId, setUserId] = React.useState('');
  const [severity, setSeverity] = React.useState<AlertSeverity>('WARNING');
  const [quiet, setQuiet] = React.useState<QuietHours | null>(null);
  const add = useAlertWrite<Record<string, unknown>>('/recipients', 'POST');
  // Nobody left to add: no box at all (an empty picker read as "something is broken").
  if (data === undefined || data.candidates.length === 0) return null;
  // People first, then the dev fixture accounts, each labelled.
  const candidates = [...data.candidates].sort(
    (a, b) => Number(isFixtureAccount(a.email)) - Number(isFixtureAccount(b.email)),
  );
  return (
    <div
      style={{
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-end',
        flexWrap: 'wrap',
        marginTop: '16px',
        padding: '12px',
        borderRadius: 'var(--radius-card)',
        backgroundColor: 'var(--color-bg-secondary)',
      }}
    >
      <label style={{ minWidth: '220px' }}>
        <span style={labelStyle}>Add another Paelode admin</span>
        <select
          aria-label="Platform admin to add"
          value={userId}
          onChange={(e): void => setUserId(e.target.value)}
          style={fieldStyle}
        >
          <option value="">Choose a platform admin…</option>
          {candidates.map((c) => (
            <option key={c.userId} value={c.userId}>
              {`${c.name} (${c.email})${c.userId === myUserId ? ' — you' : ''}${
                isFixtureAccount(c.email) ? ' — test account' : ''
              }`}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span style={labelStyle}>Alerts</span>
        <select
          aria-label="Alerts to receive"
          value={severity}
          onChange={(e): void => setSeverity(e.target.value as AlertSeverity)}
          style={fieldStyle}
        >
          <option value="WARNING">Warnings and critical</option>
          <option value="CRITICAL">Critical only</option>
        </select>
      </label>
      <QuietHoursFields value={quiet} onChange={setQuiet} who="New recipient" />
      <Button
        disabled={userId === '' || add.isPending}
        onClick={(): void =>
          add.run(
            { userId, minSeverityEmail: severity, quietHours: quiet },
            () => {
              toast.success('Recipient added');
              setUserId('');
              setQuiet(null);
            },
            (e) => toast.error(errText(e)),
          )
        }
      >
        Add
      </Button>
    </div>
  );
}

/**
 * The first thing on the tab: whether the person looking at it gets alerts, and where.
 * Corbin was already a recipient and could not tell from the table (2026-10-20).
 */
function YourAlertStatus({
  recipients,
  myUserId,
  canManage,
}: {
  recipients: readonly AlertRecipient[];
  myUserId: string | undefined;
  canManage: boolean;
}): React.JSX.Element | null {
  const { data: settings } = useAlertSettings();
  if (myUserId === undefined) return null;
  const me = recipients.find((r) => r.userId === myUserId);
  const text =
    me === undefined
      ? canManage
        ? "You don't receive alerts — add yourself below."
        : "You don't receive alerts."
      : me.emailEnabled
        ? `You receive ${severityPhrase(me.minSeverityEmail)} at ${me.email}.`
        : 'You are listed, but email alerts are turned off for you.';
  return (
    <p style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>
      {text}
      {settings?.alertsEnabled === false &&
        ' Alerts are switched off for everyone right now (see Rules).'}
    </p>
  );
}

export function RecipientsTab(): React.JSX.Element {
  const canManage = useCanManageAlerts();
  const myUserId = useMyUserId();
  const { data, isLoading, isError } = useAlertRecipients();
  return (
    <div>
      {data !== undefined && (
        <YourAlertStatus recipients={data.recipients} myUserId={myUserId} canManage={canManage} />
      )}
      <p style={{ ...subText, textTransform: 'none', marginBottom: '12px' }}>
        Alerts go to each person&apos;s own sign-in address. During someone&apos;s quiet hours they
        are emailed critical alerts only; warnings in that window are not emailed, but still show on
        the Incidents tab.
      </p>
      {isLoading ? (
        <p style={subText}>Loading…</p>
      ) : isError || data === undefined ? (
        <p role="alert" style={subText}>
          Could not load recipients.
        </p>
      ) : data.recipients.length === 0 ? (
        <p role="status" style={{ fontSize: '14px', fontWeight: 600 }}>
          No one gets alerts until you add a recipient.
        </p>
      ) : (
        <Table aria-label="Recipients">
          <TableHeader>
            <TableRow>
              <TableHead>Who</TableHead>
              <TableHead>Alerts</TableHead>
              <TableHead>Quiet hours</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.recipients.map((r) => (
              <RecipientRow key={r.id} r={r} isMe={r.userId === myUserId} />
            ))}
          </TableBody>
        </Table>
      )}
      {canManage && <AddRecipient myUserId={myUserId} />}
    </div>
  );
}
